import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { ApiError } from '@/lib/api-client';
import { redeemGiftCode, type RedeemGiftResponse } from '@/features/gifts/api';
import { STRINGS } from '@/strings/strings';

/**
 * `/einloesen[?code=DREAM-XXXX-XXXX]` — P14 Block 4 redeem page.
 *
 * Inside `<ProtectedRoute />` in App.tsx: unauthenticated visitors
 * are redirected to `/auth/login?returnTo=%2Feinloesen%3Fcode%3DDREAM-...`
 * and land back here with the code preserved after login. Same
 * query-preservation pattern VerifyTokenRoute exercises.
 *
 * Backend does all normalisation. This page passes the raw input
 * through to `POST /api/gifts/redeem`; on error, the ApiError's
 * envelope.user_message is rendered verbatim (PWA convention — the
 * five gift catalog messages are already customer-facing German).
 *
 * States: idle | checking | success. Errors surface as inline text
 * on the idle state so the customer can correct + retry.
 *
 * Mobile-first: `ContentContainer maxWidth="narrow"` mirrors
 * signup / reset-password / verify.
 */
type State =
  | { kind: 'idle'; error: string | null }
  | { kind: 'checking' }
  | { kind: 'success'; result: RedeemGiftResponse };

const eurFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function RedeemRoute() {
  const [searchParams] = useSearchParams();
  const initialCode = useMemo(() => searchParams.get('code') ?? '', [searchParams]);
  const [code, setCode] = useState<string>(initialCode);
  const [state, setState] = useState<State>({ kind: 'idle', error: null });

  useEffect(() => {
    document.title = STRINGS.gift.redeem.pageTitle;
  }, []);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (state.kind === 'checking') return;
    setState({ kind: 'checking' });
    try {
      const result = await redeemGiftCode(code);
      setState({ kind: 'success', result });
    } catch (err) {
      // PWA rule: render backend user_message verbatim for known
      // envelope errors. Anything else → generic fallback.
      const message =
        err instanceof ApiError && err.envelope?.user_message
          ? err.envelope.user_message
          : STRINGS.gift.redeem.genericError;
      setState({ kind: 'idle', error: message });
    }
  }

  if (state.kind === 'success') {
    const { addedCents, balanceCentsAfter } = state.result;
    const body = STRINGS.gift.redeem.successBody
      .replace('{credited_eur}', eurFormatter.format(addedCents / 100))
      .replace('{new_balance_eur}', eurFormatter.format(balanceCentsAfter / 100));
    return (
      <ContentContainer maxWidth="narrow">
        <div
          className="flex min-h-screen flex-col items-stretch gap-4 py-8 md:gap-6 md:py-16"
          data-testid="redeem-success"
        >
          <h1 className="text-[22px] font-semibold text-dream-navy">
            {STRINGS.gift.redeem.successHeading}
          </h1>
          <p className="whitespace-pre-line text-base leading-relaxed text-dream-navy">{body}</p>
          <div className="flex flex-col gap-3">
            {/* Single CTA — Sebastian ruling 2026-09-25 (E2E-test
                finding 1). Vouchers are book-only; sending redeemers
                to /account (audio-shaped dashboard) is off-flow.
                Removed the former secondary "Zum Konto" CTA. */}
            <Button asChild className="h-12 text-base font-semibold">
              <Link to="/book" data-testid="redeem-success-primary">
                {STRINGS.gift.redeem.primaryCta}
              </Link>
            </Button>
          </div>
        </div>
      </ContentContainer>
    );
  }

  const isChecking = state.kind === 'checking';
  const inlineError = state.kind === 'idle' ? state.error : null;

  return (
    <ContentContainer maxWidth="narrow">
      <div className="flex min-h-screen flex-col items-stretch gap-4 py-8 md:gap-6 md:py-16">
        <h1 className="text-[22px] font-semibold text-dream-navy">{STRINGS.gift.redeem.heading}</h1>
        <p className="text-base leading-relaxed text-dream-navy/80">{STRINGS.gift.redeem.intro}</p>
        <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-dream-navy">{STRINGS.gift.redeem.inputLabel}</span>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={STRINGS.gift.redeem.inputPlaceholder}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              disabled={isChecking}
              className="h-12 rounded-lg border border-dream-navy/20 bg-dream-white px-3 text-base tracking-wide text-dream-navy focus:border-dream-gold focus:outline-none disabled:opacity-60"
              data-testid="redeem-code-input"
              aria-invalid={inlineError !== null}
              aria-describedby={inlineError !== null ? 'redeem-error' : 'redeem-helper'}
            />
            <span id="redeem-helper" className="text-xs text-dream-navy/60">
              {STRINGS.gift.redeem.helper}
            </span>
          </label>
          {inlineError !== null && (
            <p
              role="alert"
              id="redeem-error"
              className="text-sm text-error"
              data-testid="redeem-error"
            >
              {inlineError}
            </p>
          )}
          <Button
            type="submit"
            className="h-12 text-base font-semibold"
            disabled={isChecking || code.trim().length === 0}
            data-testid="redeem-submit"
          >
            {isChecking ? STRINGS.gift.redeem.checkingBody : STRINGS.gift.redeem.submitButton}
          </Button>
        </form>
      </div>
    </ContentContainer>
  );
}
